// src/pages/mc/DemandDetailPanel.tsx
//
// Correção UX v2 (PRINCÍPIO ZERO) — modal de detalhe da demanda LITERAL do
// wireframe RAIOS-UX-SIDEBAR-SPEC.html (dialog #dlgDemand, class "wide"):
//   - header FIXO (.dl-h): ícone + código DEM-xxxx + ✕;
//   - tabs FIXAS (.dm-tabs): Detalhes · Métricas · Atividade;
//   - corpo ROLÁVEL (.dm-panel — max-height 62vh, overflow-y auto) — C3:
//     header fixo + body scrollável, a aba Detalhes não corta mais conteúdo;
//   - footer FIXO (.dl-f): botão Fechar.
//
// Anatomia por aba (literal):
//   Detalhes  → .dm-title + .metab (Status/Prioridade/Runtime/Supervisão/
//               Executor) + .fld Objetivo/Descrição/Escopo (.scope2) /
//               Critérios de aceite (.crit).
//   Métricas  → .mxgrid com Steps concluídos · Tempo de execução · Custo
//               acumulado · Tokens · Tentativas.
//   Atividade → timeline .tl (.tlr/.tldot/.tlmsg/.tltime), mais recente
//               primeiro (ex-aba Trace — C4).
//
// W1.5 (C6): dados de execução via GET /v1/demands/:id/executions
// (api.getDemandExecutions) — ZERO supabase.from() neste caminho. Fallback
// graceful pré-restart: 404 do endpoint → queries antigas isoladas em
// demand-executions-legacy.ts (REMOVER pós-restart), normalizadas pro mesmo
// shape (DemandExecutionRow).
//
// Props públicas preservadas: { demand, onClose } (consumido por
// MCDemandsPage, DashboardPage e BusinessDemandsTab).

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  api,
  ApiError,
  type Demand,
  type DemandProgress,
  type PullRequest,
  type DemandArtifact,
  type DemandBlockedInfo,
} from '@/lib/api';
import { PullRequestCard } from '@/components/pull-requests/PullRequestCard';
import { ArtifactViewer } from '@/components/artifacts/ArtifactViewer';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Check,
  ClipboardList,
  Target,
  X,
  GitPullRequest,
  FileText,
  Download,
  FileCode,
  FileImage,
  File as FileIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { STATUS_LABEL, columnIdForStatus } from './demand-constants';
import { useNowTick } from '@/hooks/useNowTick';
import { WfMetric } from '@/components/wf';
import { fmtDateTime, fmtDuration, formatCostStrict, formatTokens } from '@/lib/format';
import {
  aggregateUnifiedExecutions,
  attemptsFromTasks,
  realCountsFromTasks,
  type DemandExecutionRow,
} from './demand-metrics';
import { fetchDemandExecutionsLegacy } from './demand-executions-legacy';
import { interpretBlocked } from './demand-blocked-info';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { formatExecutionLead, summarizeActivityError } from '@/lib/activity';

import '@/styles/wf-demands.css';

/* ── Types ────────────────────────────────────────────────────────────────── */

interface DemandDetailPanelProps {
  demand: Demand | null;
  onClose: () => void;
}

/** Task mínima usada pelo painel (ambas as fontes — endpoint e legacy). */
interface DemandTaskLite {
  id: string;
  status: string;
  retry_count: number | null;
  // AGT-14: campos pro timeline narrativo da aba Atividade (wireframe dm-atv).
  title: string | null;
  step_number: number | null;
  target_slug: string | null;
  created_at: string | null;
  updated_at: string | null;
}

function toTaskLite(t: { id: string; status: string; retry_count?: unknown }): DemandTaskLite {
  const r = t as unknown as Record<string, unknown>;
  return {
    id: t.id,
    status: t.status,
    retry_count: typeof t.retry_count === 'number' ? t.retry_count : null,
    title: typeof r['title'] === 'string' ? (r['title'] as string) : null,
    step_number: typeof r['step_number'] === 'number' ? (r['step_number'] as number) : null,
    target_slug: typeof r['target_slug'] === 'string' ? (r['target_slug'] as string) : null,
    created_at: typeof r['created_at'] === 'string' ? (r['created_at'] as string) : null,
    updated_at: typeof r['updated_at'] === 'string' ? (r['updated_at'] as string) : null,
  };
}

// Reforma UX §5.4 — abas do dlgDemand. Trace foi RENOMEADA pra Atividade (C4).
// DEM-2037: + abas PRs e Artefatos (visualização read-only de pull_requests e
// artifacts da demanda, via api.ts — sem supabase.from() direto no front).
const TAB_ITEMS = [
  { value: 'details', label: 'Detalhes' },
  { value: 'metrics', label: 'Métricas' },
  { value: 'activity', label: 'Atividade' },
  { value: 'prs', label: 'PRs' },
  { value: 'artifacts', label: 'Artefatos' },
];

const PRIORITY_PT: Record<string, string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const RUNTIME_PT: Record<string, string> = {
  raios: 'RAIOS',
  openclaw: 'OpenClaw',
  manual: 'Manual',
};

const SUPERVISION_PT: Record<string, string> = {
  supervised: 'Supervised',
  unsupervised: 'Unsupervised',
  auto: 'Auto',
};

/** Tom do .dot de Status (wireframe .dot.run/.ok/.wait + extensão bad). */
function statusDotClass(status: string): string | null {
  const col = columnIdForStatus(status);
  if (col === 'em_execucao') return 'wf-dot--run';
  if (col === 'aguardando_aprovacao') return 'wf-dot--wait';
  if (col === 'aprovadas' || col === 'finalizadas') return 'wf-dot--ok';
  // DEM-2614: falharam (failed) é terminal com laudo — mesma severidade visual.
  if (col === 'reprovadas' || col === 'bloqueadas' || col === 'falharam') return 'wf-dot--bad';
  return null; // prontas/canceladas — dot neutro (muted)
}

function priorityDotClass(priority: string): string | null {
  if (priority === 'critical' || priority === 'high') return 'wf-dot--high';
  if (priority === 'medium') return 'wf-dot--wait';
  return null;
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

export function DemandDetailPanel({ demand, onClose }: DemandDetailPanelProps) {
  const [tab, setTab] = useState('details');
  // F5 (#1250) emagreceu a LIST de demandas (sem description/context/outcome/
  // scope) — o painel NÃO pode renderizar só a row da lista, senão demanda
  // cheia no banco vira "Não informado" na tela (regressão real: DEM-2181).
  // Ao abrir, busca a demanda COMPLETA por id e faz merge por cima da row.
  const [fullDemand, setFullDemand] = useState<Demand | null>(null);
  useEffect(() => {
    setFullDemand(null);
    if (!demand) return;
    let alive = true;
    void api
      .getDemand(demand.id)
      .then((d) => {
        if (alive) setFullDemand(d);
      })
      .catch(() => {
        /* degradação: segue com a row da lista (título/status funcionam) */
      });
    return () => {
      alive = false;
    };
  }, [demand?.id]);
  const effectiveDemand =
    demand && fullDemand && fullDemand.id === demand.id ? { ...demand, ...fullDemand } : demand;

  const [tasks, setTasks] = useState<DemandTaskLite[]>([]);
  const [execs, setExecs] = useState<DemandExecutionRow[]>([]);
  // DEM-2079 (#3) — motivo REAL do bloqueio vindo do endpoint /executions.
  const [blocked, setBlocked] = useState<DemandBlockedInfo | null>(null);
  const [legacyMode, setLegacyMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [fetchedFor, setFetchedFor] = useState<string | null>(null);
  // Live refresh (UX realtime 2026-06-11): contador de ciclos do polling —
  // MetricsTab usa pra re-buscar /progress junto de cada refresh.
  const [refreshTick, setRefreshTick] = useState(0);

  // W1.5 (C6): caminho NOVO — GET /v1/demands/:id/executions traz tasks +
  // execuções (handler/agent/skill) agregadas server-side, sem o cap de 1000
  // rows do PostgREST. Cache por demand.id pra não re-disparar trocando de
  // tab. Pré-restart o endpoint não existe (404) → fallback funcional nas
  // queries antigas (demand-executions-legacy.ts — remover pós-restart).
  useEffect(() => {
    if (!demand) {
      setTab('details');
      setTasks([]);
      setExecs([]);
      setLegacyMode(false);
      setLoadError(false);
      setFetchedFor(null);
      setLoading(false);
      return;
    }
    if (fetchedFor === demand.id) return;
    setFetchedFor(demand.id);
    setLoading(true);
    setLoadError(false);
    setLegacyMode(false);

    (async () => {
      try {
        const res = await api.getDemandExecutions(demand.id, { limit: 500 });
        setTasks(res.tasks.map(toTaskLite));
        setExecs(res.executions);
        setBlocked(res.blocked ?? null);
      } catch (err) {
        const is404 = err instanceof ApiError && err.status === 404;
        if (!is404) {
          console.error('[DemandDetailPanel] GET /demands/:id/executions falhou:', err);
          setTasks([]);
          setExecs([]);
          setBlocked(null);
          setLoadError(true);
          return;
        }
        // Backend pré-restart (rota ainda não existe) → fallback legacy.
        try {
          const legacy = await fetchDemandExecutionsLegacy(demand);
          setTasks(legacy.tasks.map(toTaskLite));
          setExecs(legacy.executions);
          setBlocked(null); // legacy não expõe blocked_reason
          setLegacyMode(true);
        } catch (legacyErr) {
          console.error('[DemandDetailPanel] fallback legacy falhou:', legacyErr);
          setTasks([]);
          setExecs([]);
          setBlocked(null);
          setLoadError(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [demand, fetchedFor]);

  // Refresh SILENCIOSO (sem skeleton) de executions/tasks — métricas
  // (steps/tempo/custo/tokens/tentativas) e atividade acompanham a execução.
  // Modo legacy (backend pré-restart) também é coberto.
  const demandId = demand?.id ?? null;
  const demandRef = useRef(demand);
  demandRef.current = demand;
  const silentRefresh = useCallback(async () => {
    const current = demandRef.current;
    if (!current || current.id !== demandId) return;
    try {
      const res = await api.getDemandExecutions(current.id, { limit: 500 });
      setTasks(res.tasks.map(toTaskLite));
      setExecs(res.executions);
      setBlocked(res.blocked ?? null);
      setLegacyMode(false);
    } catch (err) {
      const is404 = err instanceof ApiError && err.status === 404;
      if (!is404) return; // mantém os dados atuais — refresh é best-effort
      try {
        const legacy = await fetchDemandExecutionsLegacy(current);
        setTasks(legacy.tasks.map(toTaskLite));
        setExecs(legacy.executions);
        setBlocked(null); // legacy não expõe blocked_reason
        setLegacyMode(true);
      } catch {
        /* best-effort */
      }
    } finally {
      setRefreshTick((t) => t + 1);
    }
  }, [demandId]);

  // Polling de 12s (fallback) enquanto a modal está aberta — segura o live
  // mesmo se o canal realtime cair. agent_executions é particionada (sem
  // Realtime); tasks/handler_executions cobrem o gatilho instantâneo abaixo.
  useEffect(() => {
    if (!demandId) return;
    const id = window.setInterval(() => void silentRefresh(), 12_000);
    return () => window.clearInterval(id);
  }, [demandId, silentRefresh]);

  // DSH-03 realtime DE VERDADE na modal: evento em tasks/handler_executions
  // da demanda → refresh imediato (coalescido em 1s pra absorver rajadas de
  // execuções). Métricas e Atividade atualizam na hora, não no passo de 12s.
  const rtTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (rtTimer.current) clearTimeout(rtTimer.current);
    },
    [],
  );
  const coalescedRefresh = useCallback(() => {
    if (rtTimer.current) return;
    rtTimer.current = setTimeout(() => {
      rtTimer.current = null;
      void silentRefresh();
    }, 1_000);
  }, [silentRefresh]);
  useSupabaseRealtime(
    {
      table: 'tasks',
      filter: demandId ? `demand_id=eq.${demandId}` : undefined,
      enabled: !!demandId,
    },
    coalescedRefresh,
  );
  useSupabaseRealtime(
    {
      table: 'handler_executions',
      filter: demandId ? `demand_id=eq.${demandId}` : undefined,
      enabled: !!demandId,
    },
    coalescedRefresh,
  );

  const code = demand ? (demand.code ?? `#${demand.id.slice(0, 8)}`) : '';

  return (
    <Dialog open={!!demand} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        size="lg"
        hideClose
        className="max-w-[760px] w-[92vw] p-0 gap-0 overflow-hidden flex flex-col"
      >
        {demand && (
          <>
            <DialogTitle className="sr-only">{demand.title}</DialogTitle>
            <DialogDescription className="sr-only">
              Detalhes da demanda {code}
            </DialogDescription>

            {/* Header FIXO (wireframe .dl-h) — C3 */}
            <div className="wf-modal-header shrink-0">
              <ClipboardList className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden="true" />
              <span className="wf-row-code">{code}</span>
              <button type="button" className="wf-modal-close" onClick={onClose} aria-label="Fechar">
                <X className="ic" aria-hidden="true" />
              </button>
            </div>

            {/* Tabs FIXAS (wireframe .dm-tabs) */}
            <div className="wf-tabs shrink-0">
              {TAB_ITEMS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={cn('wf-tab', tab === t.value && 'active')}
                  onClick={() => setTab(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Corpo ROLÁVEL (wireframe .dm-panel — max-height 62vh) — C3 */}
            <div className="wf-dm-panel" data-testid="demand-detail-scroll">
              {legacyMode && (
                <div className="text-[11px] text-raios-warning-text bg-raios-warning/10 border border-raios-warning/30 rounded px-2 py-1.5 mb-3">
                  Endpoint /executions indisponível (backend pré-restart) — exibindo dados via
                  consultas diretas (modo legacy).
                </div>
              )}
              {loadError && (
                <div className="text-[11px] text-raios-danger-text bg-raios-danger/10 border border-raios-danger/30 rounded px-2 py-1.5 mb-3">
                  Falha ao carregar execuções da demanda — métricas e atividade podem estar
                  incompletas.
                </div>
              )}
              {tab === 'details' && <DetailsTab demand={effectiveDemand ?? demand} blocked={blocked} />}
              {tab === 'metrics' && (
                // key remonta a tab ao trocar de demanda — estado interno
                // (progress/fallback) nasce fresco, sem reset síncrono em effect.
                <MetricsTab key={demand.id} demand={effectiveDemand ?? demand} tasks={tasks} execs={execs} loading={loading} refreshTick={refreshTick} />
              )}
              {tab === 'activity' && <ActivityTab demand={effectiveDemand ?? demand} tasks={tasks} execs={execs} loading={loading} />}
              {/* DEM-2037 — abas novas (lazy: cada uma busca ao montar). */}
              {tab === 'prs' && <PRsTab key={`prs-${demand.id}`} demandId={demand.id} />}
              {tab === 'artifacts' && <ArtifactsTab key={`art-${demand.id}`} demandId={demand.id} />}
            </div>

            {/* Footer FIXO (wireframe .dl-f) */}
            <div className="wf-modal-footer shrink-0">
              <button type="button" className="wf-btn wf-btn-ghost" onClick={onClose}>
                Fechar
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Details Tab (wireframe #dm-det) ──────────────────────────────────────── */

/** Quebra texto livre de escopo em itens de lista (um por linha/; marcador). */
function scopeItems(text: string): string[] {
  return text
    .split(/\n+/)
    .map((l) => l.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean);
}

/** Cache module-level id→nome de negócio (uma busca por sessão — DSH-02). */
let businessNamesPromise: Promise<Map<string, string>> | null = null;
function getBusinessNames(): Promise<Map<string, string>> {
  businessNamesPromise ??= api
    .listBusinesses()
    .then((list) => new Map((list ?? []).map((b) => [b.id, b.name])))
    .catch(() => {
      businessNamesPromise = null; // permite retry na próxima abertura
      return new Map<string, string>();
    });
  return businessNamesPromise;
}

/** demands.business_id agora declarado no type Demand (DEM-2001); leitura
 *  direta (mantém a função por compat com chamadas existentes). */
function demandBusinessId(d: Demand): string | null {
  return d.business_id ?? null;
}

/** Bloco de campo semântico com ESTADO EXPLÍCITO quando ausente (DEM-2001).
 *  Em vez de sumir do detalhe, o campo aparece com um marcador "Não informado"
 *  — o usuário sabe que ele existe e está vazio, não que a UI o ignorou. */
function SemanticField({ label, value }: { label: string; value: string | null | undefined }) {
  const filled = typeof value === 'string' && value.trim().length > 0;
  return (
    <div className="wf-fld" data-testid={`demand-field-${label}`}>
      <div className="fk">{label}</div>
      {filled ? (
        <p className="whitespace-pre-wrap">{value}</p>
      ) : (
        <p className="text-raios-muted italic" data-testid={`demand-field-${label}-absent`}>
          Não informado
        </p>
      )}
    </div>
  );
}

function DetailsTab({ demand, blocked }: { demand: Demand; blocked: DemandBlockedInfo | null }) {
  // DEM-2079 (#3) — motivo REAL do bloqueio. Quando o backend devolve o
  // blocked_reason estruturado, interpretamos e mostramos a causa verdadeira +
  // detalhe técnico colapsável. Só caímos no bloco de HIPÓTESES antigo quando a
  // demand está bloqueada, NÃO há motivo estruturado E nenhuma task foi
  // materializada (falha genuína do spawner antes de criar sub-tasks).
  const interpreted = demand.status === 'blocked' && blocked ? interpretBlocked(blocked) : null;
  const blockedNoTasks =
    demand.status === 'blocked' && !blocked && (demand.tasks_created ?? 0) === 0;

  const objective = demand.objective ?? demand.description;
  const showDescription =
    !!demand.description && !!demand.objective && demand.description !== demand.objective;

  // DSH-02: 5º bloco "Negócio" — nome resolvido via cache de listBusinesses.
  const businessId = demandBusinessId(demand);
  const [businessNames, setBusinessNames] = useState<Map<string, string> | null>(null);
  useEffect(() => {
    let alive = true;
    void getBusinessNames().then((names) => {
      if (alive) setBusinessNames(names);
    });
    return () => {
      alive = false;
    };
  }, []);
  const businessName = businessId ? (businessNames?.get(businessId) ?? null) : null;

  return (
    <div>
      {/* DEM-2079 (#3) — motivo REAL do bloqueio: causa interpretada + detalhe
          técnico colapsável (Expandir). Substitui o texto hardcoded antigo. */}
      {interpreted && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-xs space-y-1.5 mb-3">
          <p className="font-semibold text-red-700 dark:text-red-400">
            ⛔ {interpreted.title}
          </p>
          {interpreted.reason && (
            <p className="text-red-700/90 dark:text-red-300/90">
              <span className="font-medium">Motivo:</span> {interpreted.reason}
            </p>
          )}
          {(blocked?.blocked_at || blocked?.reason_code) && (
            <p className="text-[11px] text-red-700/70 dark:text-red-300/70">
              {blocked?.reason_code && <>Código: <code>{blocked.reason_code}</code></>}
              {blocked?.reason_code && blocked?.blocked_at && ' · '}
              {blocked?.blocked_at && <>em {fmtDateTime(blocked.blocked_at)}</>}
            </p>
          )}
          {interpreted.hasTechnicalDetail && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] text-red-700/80 dark:text-red-300/80 hover:underline select-none">
                Detalhes técnicos
              </summary>
              <pre className="mt-1 max-h-64 overflow-auto rounded bg-black/5 dark:bg-white/5 p-2 text-[10.5px] leading-snug whitespace-pre-wrap break-words text-red-900/80 dark:text-red-200/80">
                {JSON.stringify(blocked?.raw, null, 2)}
              </pre>
            </details>
          )}
          {demand.trace_id && (
            <p className="text-[11px] text-red-700/70 dark:text-red-300/70">
              Trace: <code>{demand.trace_id.slice(0, 8)}…</code> — aba Atividade pra inspecionar eventos.
            </p>
          )}
        </div>
      )}
      {blockedNoTasks && (
        <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2.5 text-xs space-y-1 mb-3">
          <p className="font-semibold text-yellow-700 dark:text-yellow-400">
            ⚠ Bloqueada sem task materializada
          </p>
          <p className="text-yellow-700/80 dark:text-yellow-400/80">
            Spawner não criou tasks. Causas comuns:
          </p>
          <ul className="text-yellow-700/80 dark:text-yellow-400/80 list-disc list-inside ml-1 space-y-0.5">
            <li>Pipeline atrelado falhou no decomposer (validar output_tasks_not_array em handler_executions).</li>
            <li>business_id ausente quando pipeline.requires_business=true.</li>
            <li>pipeline_slug aponta pra pipeline inativo ou inexistente.</li>
            <li>Demand criada via wizard que não dispara spawner (caso edge).</li>
          </ul>
          {demand.trace_id && (
            <p className="text-[11px] text-yellow-700/70 dark:text-yellow-400/70 mt-1">
              Trace: <code>{demand.trace_id.slice(0, 8)}…</code> — abra a aba Atividade pra inspecionar eventos.
            </p>
          )}
        </div>
      )}

      {/* Título (wireframe .dm-title) */}
      <p className="wf-dm-title">{demand.title}</p>

      {/* Grade de metadados (wireframe .metab) */}
      <div className="wf-metab">
        <div className="wf-meta">
          <div className="mk">Status</div>
          <div className="mv">
            <span className={cn('wf-dot', statusDotClass(demand.status))} aria-hidden="true" />
            {STATUS_LABEL[demand.status] ?? demand.status}
          </div>
        </div>
        <div className="wf-meta">
          <div className="mk">Prioridade</div>
          <div className="mv">
            <span className={cn('wf-dot', priorityDotClass(demand.priority))} aria-hidden="true" />
            {PRIORITY_PT[demand.priority] ?? demand.priority}
          </div>
        </div>
        <div className="wf-meta">
          <div className="mk">Runtime</div>
          <div className="mv">{RUNTIME_PT[demand.runtime_target] ?? demand.runtime_target}</div>
        </div>
        <div className="wf-meta">
          <div className="mk">Supervisão</div>
          <div className="mv">{SUPERVISION_PT[demand.supervision_mode] ?? demand.supervision_mode}</div>
        </div>
        {demand.execution_executor && (
          <div className="wf-meta" data-testid="demand-execution-executor">
            <div className="mk">{demand.execution_executor.type === 'squad' ? 'Squad' : 'Pipeline'}</div>
            <div className="mv">{demand.execution_executor.name} · v{demand.execution_executor.version}</div>
          </div>
        )}
        <div className="wf-meta">
          <div className="mk">Negócio</div>
          <div className="mv">{businessName ?? '—'}</div>
        </div>
        {/* F4 2026-07-19 — Size da demanda (taxonomia nova; legados mapeados). */}
        <div className="wf-meta">
          <div className="mk">Size</div>
          <div className="mv">{SIZING_PT[(demand.sizing ?? '').toLowerCase()] ?? demand.sizing ?? '—'}</div>
        </div>
      </div>

      {/* Objetivo (wireframe .fld com 🎯 #i-target âmbar) */}
      {objective && (
        <div className="wf-fld">
          <div className="fk">
            <Target className="ic" style={{ color: 'var(--wf-amber)' }} aria-hidden="true" />
            Objetivo
          </div>
          <p className="whitespace-pre-wrap">{objective}</p>
        </div>
      )}

      {/* Descrição (só se difere do objetivo exibido) */}
      {showDescription && (
        <div className="wf-fld">
          <div className="fk">Descrição</div>
          <p className="whitespace-pre-wrap">{demand.description}</p>
        </div>
      )}

      {/* DEM-2001 — campos semânticos novos da demanda, fiéis ao backend.
          Renderizados SEMPRE com estado explícito ("Não informado") quando
          ausentes — o usuário deixa de ver lacunas silenciosas. */}
      <SemanticField label="Contexto" value={demand.context} />
      <SemanticField
        label="Artefato de entrega esperado"
        value={demand.delivery_artifact_expected}
      />
      <SemanticField label="Resultado esperado" value={demand.outcome_expected} />

      {/* Escopo dentro/fora (wireframe .scope2/.scopecol). DEM-2001: estado
          explícito quando ambos ausentes, em vez de sumir do detalhe. */}
      {demand.scope_in || demand.scope_out ? (
        <div className="wf-fld" data-testid="demand-field-Escopo">
          <div className="fk">Escopo</div>
          <div className="wf-scope2">
            {demand.scope_in && (
              <div className="wf-scopecol in">
                <h5>
                  <Check className="ic" aria-hidden="true" /> Dentro do escopo
                </h5>
                <ul>
                  {scopeItems(demand.scope_in).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {demand.scope_out && (
              <div className="wf-scopecol out">
                <h5>
                  <X className="ic" aria-hidden="true" /> Fora do escopo
                </h5>
                <ul>
                  {scopeItems(demand.scope_out).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="wf-fld" data-testid="demand-field-Escopo">
          <div className="fk">Escopo</div>
          <p className="text-raios-muted italic" data-testid="demand-field-Escopo-absent">
            Não informado
          </p>
        </div>
      )}

      {/* Critérios de aceite (wireframe .crit). Sem tracking de conclusão por
          critério no schema → cbox marcada quando a demanda terminou bem. */}
      {demand.acceptance_criteria && demand.acceptance_criteria.length > 0 && (
        <div className="wf-fld">
          <div className="fk">Critérios de aceite</div>
          {demand.acceptance_criteria.map((c, i) => {
            const checked = demand.status === 'done' || demand.status === 'approved';
            return (
              <div key={i} className="wf-crit">
                <span className={cn('cbox', !checked && 'todo')} aria-hidden="true">
                  <Check className="ic" />
                </span>
                <span className="ctx">{c}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Metrics Tab (wireframe #dm-met — .mxgrid) ────────────────────────────── */
//
// Fonte dos counters: /v1/demands/:id/progress (view v_demand_progress) >
// campos da Demand row > contagem real das tasks (counters zerados — bug B12
// em demands antigas). Cost/tokens/duração: agregação canônica das execuções
// (handler como total; agent fallback — demand-metrics.aggregate*).

function MetricsTab({
  demand,
  tasks,
  execs,
  loading,
  refreshTick = 0,
}: {
  demand: Demand;
  tasks: DemandTaskLite[];
  execs: DemandExecutionRow[];
  loading: boolean;
  /** Ciclo do live-refresh do panel — re-busca /progress a cada batida. */
  refreshTick?: number;
}) {
  const [progress, setProgress] = useState<DemandProgress | null>(null);
  const [progressFallback, setProgressFallback] = useState(false);

  useEffect(() => {
    if (!demand.id) return;
    api
      .getDemandProgress(demand.id)
      .then(setProgress)
      .catch(() => {
        setProgress(null);
        setProgressFallback(true);
      });
  }, [demand.id, refreshTick]);

  const execAgg = useMemo(() => aggregateUnifiedExecutions(execs), [execs]);
  const realCounts = useMemo(() => realCountsFromTasks(tasks), [tasks]);

  const counterCreated = progress?.tasks_created ?? demand.tasks_created ?? 0;
  const counterDone = progress?.tasks_done ?? demand.tasks_done ?? 0;
  const counterStale = counterCreated === 0 && realCounts.total > 0;
  const tasksCreated = counterStale ? realCounts.total : counterCreated;
  const tasksDone = counterStale ? realCounts.done : counterDone;

  const attempts = attemptsFromTasks(tasks);
  const tokensTotal = execAgg.total.tokensIn + execAgg.total.tokensOut;

  // MTR-01 (admin 2026-06-12): "Tempo de execução" é RELÓGIO DE PAREDE, igual
  // ao card do kanban — a soma das execuções (execAgg.total.durationMs) conta
  // handlers PARALELOS e estoura o tempo real (DEM-1948: card 1h29 vs soma
  // 2h16). Espelha a semântica do DemandRichCard: terminal = updated−created;
  // em execução = agora−started_at (tick vivo); demais = agora−updated.
  const now = useNowTick(1_000);
  const colId = columnIdForStatus(demand.status);
  // DEM-2614: falharam (failed) é terminal — relógio de parede estático.
  const isTerminal =
    colId === 'finalizadas' ||
    colId === 'bloqueadas' ||
    colId === 'falharam' ||
    colId === 'canceladas' ||
    colId === 'reprovadas';
  const wallClockMs = (() => {
    if (isTerminal) {
      if (!demand.updated_at || !demand.created_at) return null;
      return Math.max(0, Date.parse(demand.updated_at) - Date.parse(demand.created_at));
    }
    const baseline = colId === 'em_execucao'
      ? (demand.started_at ?? demand.created_at)
      : demand.updated_at;
    if (!baseline) return null;
    return Math.max(0, now - Date.parse(baseline));
  })();

  if (loading) return <TabSkeleton />;

  if (tasksCreated === 0 && execs.length === 0) {
    return (
      <div className="text-sm text-raios-text-secondary py-8 text-center">
        Sem tasks materializadas ainda. Métricas aparecerão quando o spawner criar tasks pra essa
        demand.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {progressFallback && (
        <div className="text-[11px] text-raios-warning-text bg-raios-warning/10 border border-raios-warning/30 rounded px-2 py-1.5">
          Usando dados da demand (endpoint /progress indisponível).
        </div>
      )}
      {/* AGT-14 (admin): aviso técnico de counters removido — o fallback pra
          contagem real continua silencioso (não agrega nada pro usuário). */}
      {/* mxgrid LITERAL do wireframe */}
      <div className="wf-metricgrid">
        <WfMetric value={tasksCreated > 0 ? `${tasksDone}/${tasksCreated}` : '—'} label="Tarefas concluídas" />
        <WfMetric
          value={wallClockMs != null && wallClockMs > 0 ? (fmtDuration(wallClockMs) ?? '—') : '—'}
          label="Tempo de execução"
        />
        <WfMetric value={formatCostStrict(execAgg.total.costUsd)} label="Custo acumulado" />
        <WfMetric value={formatTokens(tokensTotal) ?? '0'} label="Tokens" />
        <WfMetric value={attempts != null ? String(attempts) : '—'} label="Tentativas" />
      </div>

      {execAgg.usedFallback && (
        <p className="text-[11px] text-raios-muted">
          Total via agent_executions (handler_executions vazio pra essa demanda — legacy).
        </p>
      )}
    </div>
  );
}

/* ── Activity Tab (wireframe #dm-atv — ex-Trace, C4) ──────────────────────── */
//
// Timeline reconstruída das EXECUÇÕES da demanda (handler/agent/skill — e
// tool no modo legacy), mais recente primeiro. Fonte W1.5: executions[] do
// GET /v1/demands/:id/executions.

// F4 — labels PT da taxonomia de sizing (novos + legados).
const SIZING_PT: Record<string, string> = {
  micro: 'Micro',
  simples: 'Simples',
  curta: 'Simples',
  media: 'Média',
  grande: 'Grande',
  longa: 'Grande',
  epico: 'Épico',
};

function tldotTone(status: string | null): string | null {
  if (!status) return null;
  if (['done', 'completed', 'ok', 'success'].includes(status)) return 'ok';
  if (['failed', 'timeout', 'cancelled', 'canceled'].includes(status)) return 'bad';
  if (['running', 'executing', 'pending'].includes(status)) return 'warn';
  return null;
}

// AGT-14 — timeline NARRATIVA do wireframe (dm-atv): eventos da demanda em
// linguagem de produto ("Demanda criada", "Step X concluído"), não dump
// técnico de execuções. Fonte: demand (criada) + tasks (steps do pipeline,
// title/status/timestamps).
//
// UX squads 2026-08-04 (pedido direto do dono): as AÇÕES executadas sobre a
// demanda (agente da squad rodou, tool usada, skill disparada — executions[]
// do GET /demands/:id/executions) agora entram na MESMA timeline, com o
// wording humano do feed do dashboard (lib/activity.formatExecutionLead).
// Antes elas só apareciam quando a demanda não tinha task nenhuma (fallback),
// então demanda executada por squad mostrava só "Step X" e escondia quem fez
// o quê.
interface TimelineRow {
  key: string;
  tone: string | null;
  msg: React.ReactNode;
  when: string | null;
}

function taskTimelineRow(t: DemandTaskLite): TimelineRow {
  const title = t.title?.trim() || (t.step_number != null ? `Step ${t.step_number}` : 'Step');
  const when = t.updated_at ?? t.created_at;
  const st = (t.status ?? '').toLowerCase();
  if (['done', 'completed', 'ok', 'success'].includes(st)) {
    return { key: t.id, tone: 'ok', when, msg: (<>Step <b>{title}</b> concluído.</>) };
  }
  if (st === 'failed' || st === 'timeout') {
    return { key: t.id, tone: 'bad', when, msg: (<>Step <b>{title}</b> falhou.</>) };
  }
  if (st === 'cancelled' || st === 'canceled') {
    return { key: t.id, tone: null, when, msg: (<>Step <b>{title}</b> cancelado.</>) };
  }
  if (st === 'running' || st === 'executing') {
    return {
      key: t.id,
      tone: null,
      when,
      msg: t.target_slug ? (<><b>{t.target_slug}</b> iniciou o step <b>{title}</b>.</>) : (<>Step <b>{title}</b> em execução.</>),
    };
  }
  return { key: t.id, tone: 'warn', when, msg: (<>Step <b>{title}</b> aguardando execução.</>) };
}

/** Linha de AÇÃO executada (agente/tool/skill/handler) no wording humano do
 *  feed — mesma voz do dashboard (formatExecutionLead → formatActorLead). */
function execTimelineRow(
  ev: DemandExecutionRow,
  taskTargetById: Map<string, string | null>,
): TimelineRow {
  const lead = formatExecutionLead({
    kind: ev.kind,
    status: ev.status,
    actor_name: ev.actor_name ?? null,
    actor_slug: ev.actor_slug ?? null,
    handler_name: ev.handler_name,
    agent_id: ev.agent_id,
    skill_id: ev.skill_id,
    task_id: ev.task_id,
    // Endpoint ainda não expõe o alvo do runner (pendência backend) — deriva
    // do target_slug da task ligada (tool/agent slug pra tasks de runner).
    target_slug: ev.target_slug ?? (ev.task_id ? (taskTargetById.get(ev.task_id) ?? null) : null),
  });
  const errorMsg = ev.error ? summarizeActivityError(ev.error) : null;
  return {
    key: `${ev.kind}-${ev.id}`,
    tone: tldotTone(ev.status),
    when: ev.finished_at ?? ev.started_at,
    msg: (
      <>
        <b>{lead.display}</b>
        {lead.verb ? <> {lead.verb}</> : null}
        {lead.target && <> <code>{lead.target}</code></>}
        {errorMsg && (
          <span className="block text-raios-danger-text text-[12px] break-words">{errorMsg}</span>
        )}
      </>
    ),
  };
}

function ActivityTab({
  demand,
  tasks,
  execs,
  loading,
}: {
  demand: Demand;
  tasks: DemandTaskLite[];
  execs: DemandExecutionRow[];
  loading: boolean;
}) {
  const rows = useMemo<TimelineRow[]>(() => {
    const taskTargetById = new Map<string, string | null>(tasks.map((t) => [t.id, t.target_slug]));
    // Dedup de eco: task com agent_execution já conta a história pelo AGENTE —
    // o handler '@agent-runner' da mesma task é a mesma ação em outra tabela.
    const agentTaskIds = new Set(
      execs.filter((e) => e.kind === 'agent' && e.task_id).map((e) => e.task_id as string),
    );
    const actionRows = execs
      .filter((ev) => !(
        ev.kind === 'handler' &&
        ev.handler_name === '@agent-runner' &&
        ev.task_id != null &&
        agentTaskIds.has(ev.task_id)
      ))
      .map((ev) => execTimelineRow(ev, taskTargetById));

    // Steps das tasks (linguagem de produto) + ações executadas, no MESMO eixo.
    const merged = [...tasks.map(taskTimelineRow), ...actionRows];
    merged.sort((a, b) => (Date.parse(b.when ?? '') || 0) - (Date.parse(a.when ?? '') || 0));
    // Âncora do wireframe: a criação da demanda fecha a timeline (mais antiga).
    merged.push({
      key: 'demand-created',
      tone: 'ok',
      when: demand.created_at,
      msg: (<>Demanda <b>criada</b> e aprovada para execução.</>),
    });
    return merged;
  }, [demand.created_at, tasks, execs]);

  if (loading) return <TabSkeleton />;

  if (rows.length === 0) {
    return (
      <div className="text-sm text-raios-muted text-center py-8 space-y-2">
        <p>Sem eventos registrados pra essa demanda ainda.</p>
        <p className="text-[11px] text-raios-text-secondary max-w-xs mx-auto">
          A Atividade mostra a linha do tempo da demanda — criação, steps do pipeline e
          conclusões — e aparece conforme o trabalho avança.
        </p>
      </div>
    );
  }

  return (
    <div className="wf-tl">
      {rows.map((row) => (
        <div key={row.key} className="wf-tlr">
          <span className={cn('wf-tldot', row.tone)} aria-hidden="true" />
          <div className="wf-tlbody">
            <div className="wf-tlmsg">{row.msg}</div>
            <div className="wf-tltime">{fmtDateTime(row.when)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── PRs Tab (DEM-2037) ───────────────────────────────────────────────────── */
//
// Lista os PRs linkados à demanda lendo a tabela pull_requests filtrada por
// demand_id, SOMENTE via api.getDemandPullRequests (sem supabase.from() direto
// no front — AC#4). Reusa o PullRequestCard compartilhado (extraído de
// PullRequestsPage — anti-duplicação). Estado vazio tratado.

function PRsTab({ demandId }: { demandId: string }) {
  const [prs, setPrs] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Tab é keyed por demand.id (remonta ao trocar de demanda) → loading/error
  // já nascem no estado correto; não há reset síncrono dentro do effect (evita
  // cascading render — react-hooks/set-state-in-effect).
  useEffect(() => {
    let alive = true;
    api
      .getDemandPullRequests(demandId)
      .then((data) => {
        if (alive) setPrs(data ?? []);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [demandId]);

  if (loading) return <TabSkeleton />;

  if (error) {
    return (
      <div className="text-sm text-raios-danger-text text-center py-8">
        Falha ao carregar os PRs desta demanda.
      </div>
    );
  }

  if (prs.length === 0) {
    return (
      <div className="text-sm text-raios-muted text-center py-8 space-y-2" data-testid="prs-empty">
        <GitPullRequest className="h-7 w-7 mx-auto opacity-50" aria-hidden="true" />
        <p>Nenhum PR linkado a esta demanda ainda.</p>
        <p className="text-[11px] text-raios-text-secondary max-w-xs mx-auto">
          Os PRs aparecem aqui quando os agentes abrem pull requests durante a execução.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="prs-list">
      {prs.map((pr) => (
        <PullRequestCard key={pr.id} pr={pr} showGithubLink />
      ))}
    </div>
  );
}

/* ── Artefatos Tab (DEM-2037) ─────────────────────────────────────────────── */
//
// Lista os artefatos de entrega da demanda (api.getDemandArtifacts — via api.ts,
// sem supabase.from() direto). Ao clicar, abre o ArtifactViewer FULLSCREEN
// (multi-extensão). Fechar o viewer volta ao modal de detalhe preservando a aba
// ativa (o viewer é renderizado SOBRE a modal, sem desmontá-la). Navegação
// entre artefatos sem fechar + download por extensão real ficam no viewer.
// Estado vazio tratado.

function artifactRowIcon(a: DemandArtifact) {
  const ext = (a.extension ?? '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif'].includes(ext)) return FileImage;
  if (['md', 'markdown', 'txt', 'json', 'csv', 'log', 'yml', 'yaml', 'html', 'htm'].includes(ext))
    return FileCode;
  if (ext === 'pdf') return FileText;
  return FileIcon;
}

function ArtifactsTab({ demandId }: { demandId: string }) {
  const [artifacts, setArtifacts] = useState<DemandArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // null = viewer fechado; número = índice do artefato aberto em tela cheia.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Tab keyed por demand.id (remonta) → estado inicial já correto; sem reset
  // síncrono no effect (react-hooks/set-state-in-effect).
  useEffect(() => {
    let alive = true;
    api
      .getDemandArtifacts(demandId)
      .then((data) => {
        if (alive) setArtifacts(data ?? []);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [demandId]);

  if (loading) return <TabSkeleton />;

  if (error) {
    return (
      <div className="text-sm text-raios-danger-text text-center py-8">
        Falha ao carregar os artefatos desta demanda.
      </div>
    );
  }

  if (artifacts.length === 0) {
    return (
      <div
        className="text-sm text-raios-muted text-center py-8 space-y-2"
        data-testid="artifacts-empty"
      >
        <FileText className="h-7 w-7 mx-auto opacity-50" aria-hidden="true" />
        <p>Nenhum artefato de entrega registrado ainda.</p>
        <p className="text-[11px] text-raios-text-secondary max-w-xs mx-auto">
          Os artefatos aparecem aqui quando a demanda é entregue (PRs, documentos, mídia).
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2" data-testid="artifacts-list">
        {artifacts.map((a, i) => {
          const Icon = artifactRowIcon(a);
          return (
            <button
              key={a.id}
              type="button"
              className="w-full flex items-center gap-3 text-left rounded-md border border-raios-border bg-raios-card px-3 py-2.5 hover:border-raios-accent/50 transition-colors"
              onClick={() => setViewerIndex(i)}
              data-testid="artifact-row"
            >
              <Icon className="h-4 w-4 text-raios-text-secondary shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-raios-text truncate">{a.title}</span>
                <span className="block text-[11px] text-raios-muted truncate">
                  {a.subtype ?? a.type}
                  {a.extension && <span className="uppercase"> · {a.extension}</span>}
                </span>
              </span>
              {a.url && (
                <a
                  href={a.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-raios-muted hover:text-raios-accent-text p-1"
                  aria-label={`Baixar ${a.title}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="h-4 w-4" />
                </a>
              )}
            </button>
          );
        })}
      </div>

      {/* Viewer FULLSCREEN sobre a modal — fechar volta à aba Artefatos. */}
      {viewerIndex !== null && (
        <ArtifactViewer
          artifacts={artifacts}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  );
}

/* ── Shared ───────────────────────────────────────────────────────────────── */

function TabSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-md bg-raios-card p-3 border border-raios-border space-y-2">
          <Skeleton className="w-3/4 h-4" />
          <Skeleton className="w-1/2 h-3" />
        </div>
      ))}
    </div>
  );
}
